<template>
  <div class="flex w-full items-center gap-2.5 py-1">
    <audio ref="audioRef" :src="audioUrl" @loadedmetadata="onAudioLoaded" />

    <button
      type="button"
      class="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all"
      :class="
        sent
          ? 'bg-white/95 text-[var(--p-primary-700)] shadow-sm shadow-[var(--p-primary-950)]/10 hover:bg-white active:scale-95 dark:bg-[var(--p-primary-100)] dark:text-[var(--p-primary-950)] dark:hover:bg-white'
          : 'bg-[var(--p-surface-100)] text-[var(--p-primary-color)] shadow-sm shadow-[var(--p-surface-950)]/5 hover:bg-[var(--p-primary-200)]/70 active:scale-95 dark:bg-[var(--p-primary-800)]/50 dark:text-[var(--p-primary-100)] dark:hover:bg-[var(--p-primary-600)]/60'
      "
      @click="togglePlay"
    >
      <component
        :is="isPlaying ? Pause : Play"
        :size="18"
        :stroke-width="2.5"
        fill="currentColor"
      />
    </button>

    <div class="flex-1 grid grid-rows-3 gap-0 min-w-0" style="height: 44px">
      <div></div>
      <div
        class="group flex h-5 cursor-pointer items-center self-center"
        :class="
          sent
            ? 'text-white dark:text-[var(--p-primary-100)]'
            : 'text-[var(--p-primary-color)] dark:text-[var(--p-primary-100)]'
        "
        role="slider"
        tabindex="0"
        :aria-valuemin="0"
        :aria-valuemax="Math.floor(audioDuration)"
        :aria-valuenow="Math.floor(currentTime)"
        aria-label="Progreso del audio"
        @click="seekAudio"
        @keydown.left.prevent="seekBySeconds(-5)"
        @keydown.right.prevent="seekBySeconds(5)"
      >
        <div
          class="relative h-[3px] w-full rounded-full transition-all group-hover:h-1"
          :class="
            sent
              ? 'bg-white/35 dark:bg-[var(--p-primary-100)]/25'
              : 'bg-[var(--p-surface-200)] dark:bg-[var(--p-primary-300)]/50'
          "
        >
          <div
            :class="[
              'absolute inset-y-0 left-0 rounded-full transition-all duration-100',
              sent
                ? 'bg-white dark:bg-[var(--p-primary-100)]'
                : 'bg-[var(--p-primary-color)] dark:bg-[var(--p-primary-200)]',
            ]"
            :style="{ width: `${progress}%` }"
          />
          <div
            :class="[
              'absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 shadow-sm transition-all group-hover:opacity-100 group-focus:opacity-100',
              sent
                ? 'bg-white ring-2 ring-white/70 dark:bg-[var(--p-primary-100)] dark:ring-[var(--p-primary-900)]'
                : 'bg-[var(--p-primary-color)] ring-2 ring-white dark:bg-[var(--p-primary-200)] dark:ring-[var(--p-surface-900)]',
            ]"
            :style="{ left: `${progress}%` }"
          />
        </div>
      </div>
      <div class="flex items-center justify-between gap-2 self-start">
        <button
          type="button"
          class="rounded-full px-2 py-1 text-[11px] font-semibold leading-none transition-all active:scale-95"
          :class="
            sent
              ? 'bg-white/25 text-white ring-1 ring-white/30 hover:bg-white/35 dark:bg-[var(--p-primary-100)]/15 dark:text-[var(--p-primary-50)] dark:ring-[var(--p-primary-100)]/20 dark:hover:bg-[var(--p-primary-100)]/25'
              : 'bg-[var(--p-surface-100)] text-[var(--p-surface-800)] ring-1 ring-[var(--p-surface-200)] hover:bg-[var(--p-surface-200)] dark:bg-[var(--p-surface-700)] dark:text-[var(--p-surface-50)] dark:ring-[var(--p-surface-600)] dark:hover:bg-[var(--p-surface-600)]'
          "
          @click="cyclePlaybackRate"
        >
          {{ playbackRateLabel }}
        </button>
        <span
          class="text-[11px] font-medium tracking-wide"
          :class="
            sent
              ? 'text-[var(--p-surface-0)]/80 dark:text-[var(--p-primary-100)]/80'
              : 'text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)]'
          "
        >
          {{ formatTime(isPlaying ? currentTime : audioDuration) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { Play, Pause } from 'lucide-vue-next'
import { useTimeFormatter } from '../../composables/useTimeFormatter'

interface Props {
  audioUrl: string
  duration?: number
  sent: boolean
}

const props = defineProps<Props>()

const { formatAudioDuration } = useTimeFormatter()
const playbackRates = [1, 1.5, 2] as const

const isPlaying = ref(false)
const currentTime = ref(0)
const audioDuration = ref(props.duration || 0)
const audioRef = ref<HTMLAudioElement | null>(null)
const playbackRate = ref<(typeof playbackRates)[number]>(1)

const formatTime = formatAudioDuration
const playbackRateLabel = computed(() => `${playbackRate.value}x`)

const onAudioLoaded = () => {
  if (audioRef.value && !audioDuration.value) {
    audioDuration.value = audioRef.value.duration
  }

  if (audioRef.value) {
    audioRef.value.playbackRate = playbackRate.value
  }
}

const cyclePlaybackRate = () => {
  const currentIndex = playbackRates.indexOf(playbackRate.value)
  playbackRate.value = playbackRates[(currentIndex + 1) % playbackRates.length]

  if (audioRef.value) {
    audioRef.value.playbackRate = playbackRate.value
  }
}

const togglePlay = () => {
  const audio = audioRef.value
  if (!audio) return

  if (isPlaying.value) {
    audio.pause()
    isPlaying.value = false
  } else {
    audio.playbackRate = playbackRate.value
    audio
      .play()
      .then(() => {
        isPlaying.value = true
      })
      .catch(() => {
        isPlaying.value = false
      })
  }
}

const progress = ref(0)

const updateAudioPosition = (time: number) => {
  const audio = audioRef.value
  if (!audio || audioDuration.value <= 0) return

  const nextTime = Math.min(Math.max(time, 0), audioDuration.value)
  audio.currentTime = nextTime
  currentTime.value = nextTime
  progress.value = (nextTime / audioDuration.value) * 100
}

const seekAudio = (event: MouseEvent) => {
  const target = event.currentTarget as HTMLElement
  const { left, width } = target.getBoundingClientRect()
  if (!width) return

  const ratio = (event.clientX - left) / width
  updateAudioPosition(ratio * audioDuration.value)
}

const seekBySeconds = (seconds: number) => {
  updateAudioPosition(currentTime.value + seconds)
}

onMounted(() => {
  const audio = audioRef.value
  if (!audio) return

  const updateTime = () => {
    currentTime.value = audio.currentTime
    progress.value = audioDuration.value > 0 ? (audio.currentTime / audioDuration.value) * 100 : 0
  }

  const handleEnded = () => {
    isPlaying.value = false
    currentTime.value = 0
    progress.value = 0
    audio.currentTime = 0
  }

  audio.addEventListener('timeupdate', updateTime)
  audio.addEventListener('ended', handleEnded)

  onUnmounted(() => {
    audio.removeEventListener('timeupdate', updateTime)
    audio.removeEventListener('ended', handleEnded)
  })
})
</script>
